/**
 * A PROPOSAL FROM THE MEASURE — one rule, used by the CRM and the iPad alike (18 Oct 2026).
 *
 * Jacky: *"I want PR6 to function in both App and Web"*, and on refreshing over his edits: *"measure
 * replaces what it owns, my Proposal edits stay, show me the change list to confirm"*.
 *
 * Until now the merge of a saved measure into a Proposal lived in the CRM page (ProposalBuilder's import
 * handler), so only a browser could do it and the iPad could not even link the measure it had just saved.
 * It lives here now, pure, and the API runs it for both screens (`POST /api/proposals/from-measure`).
 *
 * WHAT THE MEASURE OWNS, and is replaced on every refresh:
 *   rooms, drawn floors (areas), stairs, doors, plan, measurements, carpet layout, the existing floor per
 *   room, and every line the measure created — product zones and their options (`measure:` ids), the
 *   underlay, edge-trim and transition-trim rows (`measure:` ids).
 * WHAT STAYS YOURS: title, brief, summary, included / excluded, assumptions, responsibilities, services,
 *   accessories and products you added, prices, option margin, rounding discount. Whole-site details
 *   (subfloor, edge finish, notes) still only fill blanks, as they always have (Jacky, 18 Sep).
 */
import { AssessmentArea } from './customer-proposal.js';
import { carpetOrderSummary } from './carpet-layout.js';
import { isRollCarpetFinish, isCarpetTileFinish } from './room-finish.js';
import { ProposalPricing, ProposalSelection, UNDECIDED_COLOUR } from './base-pricing.js';
import { ProposalAccessory } from './proposal-services.js';
import { refreshCommercialMeasurements } from './proposal-commercial.js';
import { workMeasurements } from './proposal-work-measurements.js';
import { pricingRooms } from './proposal-scope.js';
import { assessmentAccessoryRuns } from './assessment-edge-scope.js';
/** The plan that travels with the Proposal (moved from the CRM page; unchanged). */
export function buildPlan(floors, rooms) {
    return floors.map((f) => ({
        name: f.name,
        scale_mm_per_px: null,
        walls: f.walls.map((w) => ({ a: w.a, b: w.b, thickness_m: w.thickness_m })),
        measured_lines: (f.measured_lines ?? []).map((m) => ({ ...m })),
        doors: f.doors.map((d) => ({ from: d.from, to: d.to, kind: d.kind })),
        trims: (f.trims ?? []).map((t) => ({ ...t, kind: t.kind, points: t.points, length_m: t.length_m })),
        robes: (f.open_edges ?? []).filter((e) => e.robe_rect && (e.robe_m2 ?? 0) > 0).map((e) => ({ points: e.robe_rect, m2: e.robe_m2 ?? 0 })),
        cutouts: (f.cutouts ?? []).map((c) => ({ ...c })),
        open_edges: (f.open_edges ?? []).filter((e) => !e.robe_rect).map((e) => ({ a: e.a, b: e.b, kind: e.kind })),
        skirting: f.skirting
            ? { register: f.skirting.register, run_lm: f.skirting.run_lm, skirting_lm: f.skirting.skirting_lm, scotia_lm: f.skirting.scotia_lm,
                rooms: f.skirting.rooms.map((r) => ({ area_id: r.area_id, name: r.name, outline_m: r.outline_m, doorway_m: r.doorway_m, trim_m: r.trim_m, skirting_m: r.skirting_m, scotia_m: r.scotia_m })) }
            : { register: '', run_lm: 0, skirting_lm: 0, scotia_lm: 0, rooms: [] },
        rooms: rooms.filter((r) => r.floor_name === f.name && (r.shape ?? []).length >= 3).map((r) => ({
            id: r.id, name: r.name, shape: r.shape, shape_holes: r.shape_holes, boundary_hole_m2: r.boundary_hole_m2, area_m2: r.area_m2 ?? 0,
            finish: r.quoted_finish ?? r.flooring ?? null, ceiling: null,
            out_of_scope: r.out_of_scope === true, is_void: r.is_void === true,
            robe_m2: r.robe_m2 ?? 0, door_frame_m2: r.door_frame_m2 ?? 0,
            cutout_m2: r.cutout_m2 ?? 0, nested_m2: r.nested_m2 ?? 0,
        })),
    }));
}
/** Merge imported floors into the assessment's own list, keeping the ones already recorded (moved from the CRM). */
export function mergeAreas(existing, imported) {
    const byId = new Map(existing.map((a) => [a.id, a]));
    for (const a of imported) {
        if (byId.has(a.id))
            continue;
        const base = existing[0];
        byId.set(a.id, AssessmentArea.parse({ ...(base ? { ...base, id: a.id, name: a.name, notes: '' } : { id: a.id, name: a.name }) }));
    }
    return [...byId.values()];
}
const rec = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const arr = (v) => (Array.isArray(v) ? v : []);
const str = (v) => (typeof v === 'string' && v.trim() ? v : undefined);
const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const idKey = (v) => String(v ?? '').toLowerCase();
const product = (v) => {
    const p = rec(v), id = str(p.productID), name = str(p.name);
    return id && name ? { productID: id, name, type: str(p.type) ?? null, subType: str(p.subType) ?? null, supplier: str(p.supplier) ?? null } : undefined;
};
const underlay = (v) => {
    const u = rec(v), name = str(u.name);
    return name ? { productID: str(u.productID) ?? null, name } : undefined;
};
/** A register's answer for one room: Swift writes the exceptions map as [id, value, id, value, …]. */
function registerValue(floor, kind, roomID) {
    const r = arr(floor.registers).map(rec).find((x) => x.kind === kind);
    if (!r)
        return undefined;
    const ex = arr(r.exceptions);
    for (let i = 0; i + 1 < ex.length; i += 2)
        if (idKey(ex[i]) === idKey(roomID))
            return str(ex[i + 1]);
    return str(r.value);
}
/** Read the iPad's section answers out of a saved assessment (AssessmentV15 JSON). Missing = not answered. */
export function measureSections(assessment) {
    const a = rec(assessment);
    const out = { rooms: new Map(), edgeDefault: str(a.edgeDefault), options: [], runs: assessmentAccessoryRuns(a) };
    for (const o of arr(a.productOptions).map(rec)) {
        const p = product(o.product), zone = str(o.zoneProductID);
        if (p && zone)
            out.options.push({ id: String(o.id ?? p.productID), zoneProductID: zone, product: p, underlay: underlay(o.underlay) });
    }
    for (const f of arr(a.floors).map(rec)) {
        const scale = num(f.scaleMmPerPx) ?? null;
        for (const area of arr(f.areas).map(rec)) {
            const id = String(area.id ?? ''), nf = rec(area.newFloor);
            const action = ['remove', 'keep', 'lay_over'].find((x) => x === nf.action) ?? (area.finish === 'None' ? 'keep' : 'remove');
            const supply = nf.supply === 'supply' ? 'supply' : 'supply_install';
            const existing = registerValue(f, 'existingFloor', id);
            out.rooms.set(idKey(id), { product: product(nf.product), underlay: underlay(nf.underlay), supply, action, edge: str(area.edgeFinish) ?? out.edgeDefault, name: String(area.name ?? 'Room'),
                existingFloor: existing && existing !== 'unknown' ? existing : undefined, subfloor: str(area.subfloor) ?? str(a.defaultSubfloor),
                scaleMmPerPx: num(area.scaleMmPerPx) ?? scale });
        }
    }
    return out;
}
// ── Building the Proposal ──
const round2 = (n) => Math.round(n * 100) / 100;
/** "4 doorways · 2 fixture edges · 1 typed" — what a measured row is made of, for its note. */
function describeRuns(runs) {
    const label = { wall_run: ['wall run', 'wall runs'], drawn: ['drawn length', 'drawn lengths'], typed: ['typed length', 'typed lengths'],
        doorway: ['doorway', 'doorways'], fixture_edge: ['fixture edge', 'fixture edges'], robe_edge: ['robe edge', 'robe edges'] };
    const counts = new Map();
    for (const r of runs)
        counts.set(r.source, (counts.get(r.source) ?? 0) + 1);
    return [...counts].map(([k, n]) => `${n} ${label[k][n === 1 ? 0 : 1]}`).join(' · ') || 'no runs';
}
const isMeasureLine = (id) => id.startsWith('measure:') || id.startsWith('measure-opt:');
/**
 * Apply the measure to a Proposal. `doc` is the saved draft (or an empty one); the result keeps every edit
 * the measure does not own and says, line by line, what it changed.
 */
export function proposalFromMeasure(doc, payload, sections) {
    const { areas, rooms: importedRooms } = payload.data;
    const ws = payload.whole_site;
    // Rooms carry what the iPad answered: the existing floor, and the product that groups them into a zone.
    const rooms = importedRooms.map((r) => {
        const s = sections.rooms.get(idKey(r.id));
        if (!s)
            return r;
        const kept = s.action === 'keep';
        return { ...r, ...(s.existingFloor ? { existing_floor: s.existingFloor } : {}),
            ...(!kept && s.product ? { product_id: s.product.productID, product_name: s.product.name } : {}) };
    });
    const seeded = { ...doc.assessment,
        subfloor: doc.assessment.subfloor || ws?.subfloor || '',
        edge_finish: doc.assessment.edge_finish || ws?.edge_finish || '',
        notes: doc.assessment.notes || ws?.notes || '' };
    let next = { ...doc, assessment: { ...seeded, areas: mergeAreas(doc.assessment.areas, areas), rooms,
            stairs: (payload.stairs ?? []).map((x) => ({ floor: x.floor ?? '', runs: x.runs ?? 0, steps: x.steps ?? 0, kinds: x.kinds ?? [], carpet_lm: x.carpet_lm ?? null })),
            doors: (payload.floors ?? []).map((f) => ({ floor: f.name, count: f.doors.length })),
            plan: buildPlan(payload.floors ?? [], rooms),
            carpet_layout: payload.carpet_layout ?? doc.assessment.carpet_layout ?? null,
            measurements: payload.measurements ?? doc.assessment.measurements } };
    // Zones: rooms that share a product. One recommended line each, its options listed beside it.
    const zones = new Map();
    for (const r of rooms) {
        const s = sections.rooms.get(idKey(r.id));
        if (!s?.product || s.action === 'keep' || r.out_of_scope || r.is_void)
            continue;
        const z = zones.get(s.product.productID) ?? { product: s.product, supply: s.supply, underlay: s.underlay, roomIDs: [] };
        z.roomIDs.push(r.id);
        zones.set(s.product.productID, z);
    }
    const scope = pricingRooms(next);
    const selections = [];
    const accessories = [];
    for (const z of zones.values()) {
        const zoneRooms = rooms.filter((r) => z.roomIDs.includes(r.id));
        const zoneRuns = sections.runs.filter((r) => z.roomIDs.some((id) => idKey(id) === idKey(r.area_id)));
        const scopeIDs = [...new Set(scope.filter((s) => s.id.endsWith(`:p:${z.product.productID.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-')}`)).map((s) => s.id))];
        const area = round2(zoneRooms.reduce((n, r) => n + (r.area_m2 ?? 0), 0));
        // Roll carpet is bought by the whole metre off the roll, not by the floor's m²: with a cut plan the line is the
        // plan's whole LM at its roll width — the two numbers customer release checks (18 Oct 2026).
        const layout = next.assessment.carpet_layout;
        const order = layout && zoneRooms.length > 0 && zoneRooms.every((r) => isRollCarpetFinish(r.flooring)) ? carpetOrderSummary(layout) : null;
        const line = (id, p, role) => ProposalSelection.parse({
            id, productId: p.productID, mode: z.supply, quantity: order ? order.wholeLm : area > 0 ? area : 1, area, quantity_source: 'assessment',
            ...(order ? { carpetRollWidthM: layout.roll_width_m } : {}),
            scope_ids: scopeIDs, rooms: [...new Set(scope.filter((s) => scopeIDs.includes(s.id)).map((s) => s.name))],
            colour: UNDECIDED_COLOUR, group: p.subType ?? p.type ?? '', role,
            description: p.name, ...(role === 'option' ? { replaces_id: `measure:${z.product.productID}`.slice(0, 80) } : {}),
        });
        selections.push(line(`measure:${z.product.productID}`.slice(0, 80), z.product, 'recommended'));
        // An option's underlay is named for its option: the same 2mm underlay under the hybrid and under its laminate option
        // read as one line listed twice (end-to-end run, 18 Oct 2026).
        const underlayRow = (bound, u, forOption) => {
            if (!u?.productID)
                return; // "No extra underlay" is an answer that needs no line.
            accessories.push(ProposalAccessory.parse({ id: `measure:ud:${bound}`.slice(0, 60), name: (forOption ? `${u.name} (with ${forOption})` : u.name).slice(0, 160), kind: 'underlay', product_id: u.productID,
                scope: 'site', on: true, bound_to: bound, unit: 'm²', quantity: area, measured_quantity: area, quantity_source: 'assessment', note: 'From the site measure' }));
        };
        underlayRow(z.product.productID, z.underlay);
        // PRE-TICKED FROM THE MEASURE (Jacky, 18 Oct 2026: the old one-page quote's switches, "should be auto now"; he ticked
        // Moisture Barrier on 115 of 297 quotes and Scotia on 118 by hand). Hard floors only; each follows its own zone.
        const hard = zoneRooms.length > 0 && !zoneRooms.some((r) => isRollCarpetFinish(r.flooring) || isCarpetTileFinish(r.flooring));
        if (hard) {
            const roomSection = (r) => sections.rooms.get(idKey(r.id));
            const concrete = zoneRooms.some((r) => /concrete/i.test(roomSection(r)?.subfloor ?? next.assessment.subfloor ?? ''));
            if (concrete && area > 0)
                accessories.push(ProposalAccessory.parse({ id: `measure:mb:${z.product.productID}`.slice(0, 60), name: 'Moisture barrier',
                    kind: 'moisture_barrier', scope: 'site', on: true, bound_to: z.product.productID, unit: 'm²', quantity: area, measured_quantity: area,
                    quantity_source: 'assessment', note: 'Concrete subfloor, from the site measure' }));
            // Scotia: the zone's measured scotia runs — the same runs the Orders list and the plan page read (one reader).
            const scotiaRuns = zoneRuns.filter((r) => r.kind === 'scotia');
            const scotia = round2(scotiaRuns.reduce((n, r) => n + r.length_m, 0));
            if (scotia > 0)
                accessories.push(ProposalAccessory.parse({ id: `measure:sc:${z.product.productID}`.slice(0, 60), name: 'Scotia', kind: 'scotia',
                    scope: 'site', on: true, bound_to: z.product.productID, unit: 'LM', quantity: scotia, measured_quantity: scotia, run_ids: scotiaRuns.map((r) => r.id),
                    quantity_source: 'assessment', note: `From the site measure: ${describeRuns(scotiaRuns)}` }));
        }
        for (const o of sections.options.filter((x) => x.zoneProductID === z.product.productID)) {
            selections.push(line(`measure-opt:${o.id}`.slice(0, 80), o.product, 'option'));
            underlayRow(o.product.productID, o.underlay, o.product.name);
        }
        // Trims in this zone's rooms, by profile — every measured run, so the Proposal and the order count the same set.
        const byProfile = new Map();
        for (const r of zoneRuns.filter((x) => x.kind === 'trims' && x.length_m > 0))
            byProfile.set(r.profile, [...(byProfile.get(r.profile) ?? []), r]);
        for (const [profile, runs] of byProfile) {
            const length = round2(runs.reduce((n, r) => n + r.length_m, 0));
            accessories.push(ProposalAccessory.parse({ id: `measure:tr:${profile}:${z.product.productID}`.toLowerCase().replace(/[^a-z0-9:-]+/g, '-').slice(0, 60),
                name: profile, kind: 'trims', trim_profile: profile, scope: 'site', on: true, bound_to: z.product.productID, unit: 'LM',
                quantity: length, measured_quantity: length, run_ids: runs.map((r) => r.id), quantity_source: 'assessment', note: `From the site measure: ${describeRuns(runs)}` }));
        }
    }
    // A refresh rebuilds what the measure owns but keeps what the person decided about it: an unticked row stays unticked,
    // a typed quantity (quantity_source: manual) stays typed, a specification chosen stays chosen. Untick, type, choose
    // once — the measure never undoes it (18 Oct 2026).
    const prior = new Map(doc.accessories.filter((a) => isMeasureLine(a.id)).map((a) => [a.id, a]));
    for (const [i, a] of accessories.entries()) {
        const was = prior.get(a.id);
        if (!was)
            continue;
        accessories[i] = ProposalAccessory.parse({ ...a, on: was.on,
            ...(was.quantity_source === 'manual' && a.quantity_source === 'assessment' ? { quantity: was.quantity, quantity_source: 'manual' } : {}),
            ...(!a.product_id && was.product_id ? { product_id: was.product_id, variant_id: was.variant_id ?? null, name: was.name } : {}),
            ...(a.product_id && was.product_id === a.product_id && was.variant_id ? { variant_id: was.variant_id } : {}) });
    }
    const pricing = ProposalPricing.parse({ ...(doc.pricing ?? { products: [], services: [] }),
        products: [...(doc.pricing?.products ?? []).filter((p) => !isMeasureLine(p.id)), ...selections] });
    next = { ...next, pricing, accessories: [...doc.accessories.filter((a) => !isMeasureLine(a.id)), ...accessories] };
    next = refreshCommercialMeasurements(next, workMeasurements(next));
    const binding = payload.binding ? { work_id: payload.binding.work_id, revision_id: payload.binding.revision_id, content_hash: payload.binding.content_hash } : null;
    return { content: next, binding, changes: measureChanges(doc, next) };
}
/** What the measure changed, in words — the list both screens show before anything is saved. */
export function measureChanges(before, after) {
    const out = [];
    const m2 = (n) => `${round2(n ?? 0)} m²`;
    const was = new Map(before.assessment.rooms.map((r) => [r.id, r]));
    const now = new Map(after.assessment.rooms.map((r) => [r.id, r]));
    for (const r of after.assessment.rooms) {
        const old = was.get(r.id);
        if (!old)
            out.push({ kind: 'add', text: `Room ${r.name}${r.out_of_scope ? ' (not in scope)' : ` · ${m2(r.area_m2)}`}` });
        else if (round2(old.area_m2 ?? 0) !== round2(r.area_m2 ?? 0))
            out.push({ kind: 'change', text: `Room ${r.name}: ${m2(old.area_m2)} → ${m2(r.area_m2)}` });
        else if (!!old.out_of_scope !== !!r.out_of_scope)
            out.push({ kind: 'change', text: `Room ${r.name}: ${r.out_of_scope ? 'now not in scope' : 'back in scope'}` });
    }
    for (const r of before.assessment.rooms)
        if (!now.has(r.id))
            out.push({ kind: 'remove', text: `Room ${r.name}` });
    const lines = (doc) => new Map((doc.pricing?.products ?? []).filter((p) => isMeasureLine(p.id)).map((p) => [p.id, p]));
    const pb = lines(before), pa = lines(after);
    for (const [id, p] of pa) {
        const label = `${p.role === 'option' ? 'Option' : 'Product'} ${p.description ?? p.productId}`;
        const old = pb.get(id);
        if (!old)
            out.push({ kind: 'add', text: `${label} · ${p.mode === 'supply' ? 'supply only' : 'supply and install'} · ${m2(p.area)}` });
        else if (round2(old.area) !== round2(p.area) || old.mode !== p.mode)
            out.push({ kind: 'change', text: `${label}: ${m2(old.area)} → ${m2(p.area)}${old.mode !== p.mode ? `, ${p.mode === 'supply' ? 'supply only' : 'supply and install'}` : ''}` });
    }
    for (const [id, p] of pb)
        if (!pa.has(id))
            out.push({ kind: 'remove', text: `${p.role === 'option' ? 'Option' : 'Product'} ${p.description ?? p.productId}` });
    const acc = (doc) => new Map(doc.accessories.filter((a) => isMeasureLine(a.id)).map((a) => [a.id, a]));
    const ab = acc(before), aa = acc(after);
    for (const [id, a] of aa) {
        const old = ab.get(id);
        if (!old)
            out.push({ kind: 'add', text: `${a.name} · ${round2(a.quantity)} ${a.unit}` });
        else if (round2(old.quantity) !== round2(a.quantity))
            out.push({ kind: 'change', text: `${a.name}: ${round2(old.quantity)} → ${round2(a.quantity)} ${a.unit}` });
    }
    for (const [id, a] of ab)
        if (!aa.has(id))
            out.push({ kind: 'remove', text: a.name });
    const fb = before.assessment.measurements?.floor_area_m2, fa = after.assessment.measurements?.floor_area_m2;
    if (fa !== undefined && round2(fb ?? 0) !== round2(fa))
        out.push({ kind: 'change', text: `Floor area: ${m2(fb)} → ${m2(fa)}` });
    return out;
}
//# sourceMappingURL=proposal-from-measure.js.map